import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Chip,
  IconButton, Divider, Tooltip, InputAdornment, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { v4 as uuid } from 'uuid';
import { CAMPAIGN_STATUSES, deriveStatus, computeCampaignCost } from './advertiserConstants';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { textFieldSx } from '../../utils/dropdownStyles';
import { hasRequiredName, isDateRangeValid, isPositiveNumber } from '../../utils/validators';
import { addDays, todayEastern } from '../../utils/dateUtils';
import { useCurrency } from '../../context/CurrencyContext';
import { getCampaignDateImpact } from '../../api/advertiserApi';
import CampaignDateImpactDialog, { buildAdCleanup, needsHeadsUp } from './CampaignDateImpactDialog';

const SectionLabel = ({ children, hint, required }) => (
  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10 }}>
      <LocalText>{children}</LocalText>
      {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
    </Typography>
    {hint && (
      <Tooltip title={hint} placement="right" arrow>
        <InfoOutlinedIcon sx={{ fontSize: 13, color: '#85a5c6', cursor: 'help' }} />
      </Tooltip>
    )}
  </Box>
);

const fmtMoney = (v, symbol = '$', decimalPlaces = 2) =>
  v ? `${symbol}${Number(v).toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}` : '—';

// advertiserId: needed for the date-change check (falls back to initial.advertiserId).
// approvalMode: the save is sent for approval rather than applied, which the ad-impact popup mentions.
// lockBasis: { startDate, endDate, contentStatus } of the LIVE campaign. Whether the dates can still move depends on
//   the campaign as it is now, which differs from `initial` when a rejected proposal is re-edited (initial holds the
//   proposed dates).
const CreateCampaignModal = ({ open, onClose, onSave, initial, readOnly = false, advertiserId, approvalMode = false, lockBasis = null }) => {
  const { activeCurrency } = useCurrency();
  const currencySymbol = activeCurrency?.symbol ?? '$';
  const decimalPlaces  = activeCurrency?.decimalPlaces ?? 2;
  const [nameValues,        setNameValues]        = useState([{ localeId: 'en_us', value: '' }]);
  const [startDate,         setStartDate]         = useState('');
  const [endDate,           setEndDate]           = useState('');
  const [dailyRate,         setDailyRate]         = useState('');
  const [durationModifier,  setDurationModifier]  = useState('1.0');
  // Non-null while the "ads affected by the new dates" popup is open.
  const [impact,            setImpact]            = useState(null);
  const [saving,            setSaving]            = useState(false);

  useEffect(() => {
    setImpact(null);
    if (!open) return;
    if (initial) {
      setNameValues(
        initial.localizedName?.values?.length > 0
          ? initial.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: 'en_us', value: '' }],
      );
      setStartDate(initial.startDate ?? '');
      setEndDate(initial.endDate ?? '');
      setDailyRate(initial.dailyRate != null ? String(initial.dailyRate) : '');
      setDurationModifier(initial.durationModifier != null ? String(initial.durationModifier) : '1.0');
    } else {
      setNameValues([{ localeId: 'en_us', value: '' }]);
      setStartDate('');
      setEndDate('');
      setDailyRate('');
      setDurationModifier('1.0');
    }
  }, [open, initial?.id]); // primitive id dep avoids re-reset when object ref changes

  const endBeforeStart = Boolean(startDate && endDate && new Date(endDate) < new Date(startDate));
  const dateRangeValid = isDateRangeValid(startDate, endDate);
  const isDurationModifierValid = durationModifier === '' || isPositiveNumber(durationModifier);
  const isDailyRateValid = dailyRate !== '' && Number(dailyRate) >= 0;
  const isEdit   = Boolean(initial);

  // A campaign's dates can move only until the day they take effect (the API and database enforce the same rules,
  // on the US Eastern day). For a campaign that was APPROVED:
  //   not started  both dates move; the start cannot be moved into the past
  //   started      the start is fixed; the end can be extended, or shortened down to TOMORROW (not today)
  //   ended        neither date can change
  // A campaign that was never approved has never run, so nothing is locked; a date just cannot be in the past.
  // (A status we were not given is treated as approved, i.e. locked: the safe side.)
  const today      = todayEastern();
  const tomorrow   = addDays(today, 1);
  const basis      = isEdit ? (lockBasis ?? initial) : null;
  const basisStart = basis?.startDate ? String(basis.startDate).slice(0, 10) : null;
  const basisEnd   = basis?.endDate   ? String(basis.endDate).slice(0, 10)   : null;
  const approved   = isEdit && (basis?.contentStatus ?? 'approved') === 'approved';
  const started    = approved && Boolean(basisStart) && basisStart <= today;
  const ended      = approved && Boolean(basisEnd)   && basisEnd   <  today;

  // What is wrong with the dates typed in, if anything. Only a change is judged, like on the server.
  const startIssue = !isEdit
    ? (startDate && startDate < today ? 'past' : null)
    : (basisStart && startDate && startDate !== basisStart
        ? (started ? 'locked' : startDate < today ? 'past' : null)
        : null);
  const endIssue = isEdit && basisEnd && endDate && endDate !== basisEnd
    ? (ended ? 'locked'
        : started && endDate < tomorrow ? 'tooEarly'
        : !approved && endDate < today ? 'past'
        : null)
    : null;

  const isValid  = hasRequiredName(nameValues) && dateRangeValid && isDailyRateValid && isDurationModifierValid
    && !startIssue && !endIssue;

  // A locked date always shows the campaign's live value, even when the form was filled from a re-edited proposal.
  // (Declared after the lock flags it reads; it runs after the effect above, so it wins.)
  useEffect(() => {
    if (!open || !isEdit) return;
    if (started && basisStart) setStartDate(basisStart);
    if (ended && basisEnd) setEndDate(basisEnd);
  }, [open, initial?.id, isEdit, started, ended, basisStart, basisEnd]);

  const previewStatus = deriveStatus(startDate, endDate);
  const statusMeta    = CAMPAIGN_STATUSES.find((s) => s.id === previewStatus);
  const { costPerDay, totalCost, days } = computeCampaignCost(
    dailyRate, startDate, endDate, durationModifier,
  );

  const save = async (adCleanup) => {
    try {
      await onSave({
        localizedName: {
          key: initial?.localizedName?.key ?? uuid(),
          values: nameValues,
        },
        startDate,
        endDate,
        dailyRate:        Number(dailyRate) || 0,
        durationModifier: Number(durationModifier) || 1.0,
        ...(adCleanup ? { adCleanup } : {}),
      });
      onClose();
    } catch {
      // global api-error handler shows the toast; modal stays open for retry
    }
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    // Editing an existing campaign: ask the API whether the new dates strand any of its ads' run dates.
    // Always asked (not only when the dates differ from `initial`) because a re-edited rejected proposal
    // starts from its own dates, not the live campaign's; the API answers "no impact" for an unchanged range.
    // If the check itself fails we stop, rather than saving without knowing what it would do to the ads.
    if (isEdit && initial?.id != null && !readOnly) {
      setSaving(true);
      try {
        const result = await getCampaignDateImpact(advertiserId ?? initial.advertiserId, initial.id, startDate, endDate);
        // A conflict always opens the popup. So does a move that removes nothing but leaves an ad with no run
        // dates in the new range (or earlier dates kept as history): the user should see that before it happens.
        if (result?.hasImpact || needsHeadsUp(result)) {
          setImpact(result);
          return;
        }
      } catch {
        return;   // global api-error handler shows the toast
      } finally {
        setSaving(false);
      }
    }

    setSaving(true);
    try { await save(undefined); } finally { setSaving(false); }
  };

  const handleImpactConfirmed = async (mode) => {
    // The informational form has nothing to clean up, so no adCleanup is sent.
    const adCleanup = impact?.hasImpact ? buildAdCleanup(impact, mode) : undefined;
    setImpact(null);
    setSaving(true);
    try { await save(adCleanup); } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: '#0a326d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          {readOnly ? <LocalText>View Campaign</LocalText> : isEdit ? <LocalText>Edit Campaign</LocalText> : <LocalText>New Campaign</LocalText>}
        </Typography>
        <IconButton size="small" onClick={(e) => { e.currentTarget.blur(); onClose(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

          {/* Campaign Name */}
          <SectionLabel required>Campaign Name</SectionLabel>
          <DropdownTextField
            label="Campaign Name"
            values={nameValues}
            attribute="value"
            onChange={setNameValues}
            disabled={readOnly}
            required={!readOnly}
          />

          <Divider sx={{ my: 1 }} />

          {isEdit && !readOnly && started && (
            <Alert severity="info" data-testid="date-lock-notice" sx={{ mb: 1.5, fontSize: 12 }}>
              <LocalText>
                {ended
                  ? 'This campaign has ended, so its start and end dates can no longer be changed. You can still edit its other details.'
                  : "This campaign has already started, so its start date can't be changed. The end date can be extended, or shortened down to tomorrow."}
              </LocalText>
            </Alert>
          )}

          {/* Date range */}
          <Box display="grid" gridTemplateColumns={{ xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }} gap={2} mb={1}>
            <Box>
              <SectionLabel required>Start Date</SectionLabel>
              <TextField
                fullWidth size="small" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={readOnly || started}
                InputLabelProps={{ shrink: true }}
                inputProps={started ? undefined : { min: today }}
                error={!readOnly && startIssue === 'past'}
                helperText={
                  readOnly ? ''
                  : started ? 'Locked: this campaign has already started'
                  : startDate === '' ? 'Required'
                  : startIssue === 'past' ? "Can't be in the past"
                  : ''
                }
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <SectionLabel required>End Date</SectionLabel>
              <TextField
                fullWidth size="small" type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={readOnly || ended}
                InputLabelProps={{ shrink: true }}
                inputProps={started && !ended ? { min: tomorrow } : undefined}
                error={!readOnly && (endBeforeStart || endIssue === 'tooEarly' || endIssue === 'past')}
                helperText={
                  readOnly ? ''
                  : ended ? 'Locked: this campaign has ended'
                  : endDate === '' ? 'Required'
                  : endBeforeStart ? 'Must be after start date'
                  : endIssue === 'tooEarly' ? 'Can only be shortened down to tomorrow'
                  : endIssue === 'past' ? "Can't be in the past"
                  : ''
                }
                sx={textFieldSx}
              />
            </Box>
          </Box>

          {/* Auto-derived status badge */}
          {dateRangeValid && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#f7faff', border: '1px solid #dde6f0', borderRadius: '10px', px: 2, py: 1.2, mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600 }}>
                <LocalText>Status set automatically</LocalText>
              </Typography>
              <Chip
                label={previewStatus}
                size="small"
                sx={{ height: 22, fontSize: 10, fontWeight: 700, bgcolor: `${statusMeta?.color}18`, color: statusMeta?.color, border: `1px solid ${statusMeta?.color}40` }}
              />
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Revenue fields */}
          <Box display="grid" gridTemplateColumns={{ xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }} gap={2} mb={1}>
            <Box>
              <SectionLabel
                required
                hint="Base amount the advertiser pays per day for this campaign."
              >
                Daily Rate
              </SectionLabel>
              <TextField
                fullWidth size="small" type="number" placeholder="e.g. 3600"
                value={dailyRate}
                onChange={(e) => setDailyRate(e.target.value)}
                disabled={readOnly}
                inputProps={{ min: 0, step: 0.01 }}
                error={!readOnly && dailyRate !== '' && Number(dailyRate) < 0}
                helperText={
                  readOnly ? ''
                  : dailyRate === '' ? <LocalText>Required</LocalText>
                  : Number(dailyRate) < 0 ? <LocalText>Must be 0 or greater</LocalText>
                  : ''
                }
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: '#5383b3', fontSize: 14, fontWeight: 700 }}>{currencySymbol}</Typography></InputAdornment> }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <SectionLabel
                hint="Multiplier applied to the daily rate (e.g. 1.2 = 20% uplift). Defaults to 1.0."
              >
                Duration Modifier
              </SectionLabel>
              <TextField
                fullWidth size="small" type="number" placeholder="1.0"
                value={durationModifier}
                onChange={(e) => setDurationModifier(e.target.value)}
                disabled={readOnly}
                inputProps={{ min: 0.1, step: 0.1 }}
                error={!readOnly && durationModifier !== '' && !isPositiveNumber(durationModifier)}
                helperText={!readOnly && durationModifier !== '' && !isPositiveNumber(durationModifier) ? 'Must be greater than 0' : ''}
                sx={textFieldSx}
              />
            </Box>
          </Box>

          {/* Cost preview */}
          {dailyRate !== '' && dateRangeValid && (
            <Box
              sx={{
                bgcolor: '#f0f7f0',
                border: '1px solid #c8e6c9',
                borderRadius: '10px',
                px: 2,
                py: 1.5,
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: '#5383b3', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <LocalText>Cost / Day</LocalText>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0a326d', fontSize: 15 }}>
                  {fmtMoney(costPerDay, currencySymbol, decimalPlaces)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#5383b3', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <LocalText>Duration</LocalText>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0a326d', fontSize: 15 }}>
                  {days} <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: '#85a5c6' }}>days</Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#5383b3', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  <LocalText>Est. Total</LocalText>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#2e7d32', fontSize: 15 }}>
                  {fmtMoney(totalCost, currencySymbol, decimalPlaces)}
                </Typography>
              </Box>
            </Box>
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        {readOnly ? (
          <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
              <LocalText>Cancel</LocalText>
            </Button>
            <Button
              variant="contained"
              disabled={!isValid || saving}
              onClick={handleSubmit}
              sx={{
                bgcolor: '#0a326d', color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
                '&:hover': { bgcolor: '#0d3d85' },
                '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
              }}
            >
              {isEdit ? <LocalText>Save Changes</LocalText> : <LocalText>Create Campaign</LocalText>}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>

    <CampaignDateImpactDialog
      open={Boolean(impact)}
      impact={impact}
      needsApproval={approvalMode}
      onCancel={() => setImpact(null)}
      onConfirm={handleImpactConfirmed}
    />
    </>
  );
};

export default CreateCampaignModal;
